import { SCUILoadingLinear } from '@/atomic/atoms/SCUILoadingLinear/SCUILoadingLinear';
import { SCUIBreadCrumb } from '@/atomic/molecules/SCUIBreadCrumb/SCUIBreadCrumb';
import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';
import { PageUrl } from '@/types/enums/pageUrl-enums';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrowListPage, GrowEditModal, GrowLogModal } from './components';
import { GrowManagementCommand } from '@/services/command';
import Swal from 'sweetalert2';
import { GrowManagementQuery } from '@/services/query';
import { Box } from '@mui/material';
import { GROW } from './growManagement.classes';

const initialFormState = {
    warehouseId: -1,
    warehouseName: '',
    warehouseCode: '',
    status: false,
    totalQuantity: 0,
    growBufferSize: 0,
    growDailyLimit: null as number | null,
    stockSource: -1,
};

const getResponseMessage = (message: any, language: string): string => {
    if (!message) return '';
    if (typeof message === 'string') return message;
    return message?.[language] || message?.['en-US'] || Object.values(message)[0] || '';
};

const getErrorText = (res: any, language: string, fallback: string): string => {
    if (Array.isArray(res?.errors) && res.errors.length > 0) {
        return res.errors
            .map((err: any) => (typeof err === 'string' ? err : err?.message || JSON.stringify(err)))
            .join('');
    }
    return getResponseMessage(res?.message, language) || fallback;
};

export const GrowManagement = () => {
    const language: any = localStorage.getItem('lan');
    const countryId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;

    const [formState, setFormState] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [selectedLogWarehouseCode, setSelectedLogWarehouseCode] = useState('');
    const [growList, setGrowList] = useState<any[]>([]);

    const { t } = useTranslation();

    const onModalOpen = (row: any) => {
        setFormState({
            ...row,
            growDailyLimit: row.growDailyLimit ?? null,
            stockSource: row.growStockSourceId ?? -1,
        });
        setOpen(true);
    };

    const onModalClose = () => {
        setOpen(false);
        setFormState(initialFormState);
    };

    const onLogOpen = (row: any) => {
        setSelectedLogWarehouseCode(row.warehouseCode);
        setLogOpen(true);
    };

    const onLogClose = () => {
        setLogOpen(false);
        setSelectedLogWarehouseCode('');
    };

    const getGrowList = () => {
        setIsLoading(true);
        GrowManagementQuery.GetWarehouseList({
            countryId,
        })
            .then((res: any) => {
                if (res.data) {
                    const sortedList = res.data?.warehouseList?.sort((a: any, b: any) => a.warehouseId - b.warehouseId);
                    setGrowList(sortedList || []);

                    if (sortedList?.length === 0) {
                        Swal.fire({
                            title: t('general.noDataFound'),
                            text: '',
                            icon: 'info',
                            showConfirmButton: false,
                            timer: 3000,
                        });
                    }
                } else {
                    Swal.fire(t('growManagement.serverError'), t('general.error'), 'error');
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                Swal.fire(t('growManagement.serverError'), t('general.error'), 'error');
            });
    };

    const onUpdate = () => {
        if (formState.stockSource === -1 || formState.stockSource == null) {
            Swal.fire({
                title: t('general.warning'),
                text: t('growManagement.stockSourceRequired'),
                icon: 'warning',
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        setIsLoading(true);

        GrowManagementCommand.UpdateGrowWarehouseInfos({
            countryId,
            code: formState.warehouseCode,
            username: '',
            status: formState.status,
            bufferSize: +formState.growBufferSize,
            dailyLimit: formState.growDailyLimit == null ? null : Number(formState.growDailyLimit),
            stockSource: formState.stockSource,
        } as any)
            .then((res: any) => {
                if (res?.isSuccess === false) {
                    Swal.fire({
                        title: t('general.error'),
                        text: getErrorText(res, language, t('general.error')),
                        icon: 'error',
                        timer: 3000,
                        showConfirmButton: false,
                    });
                } else if (res?.isSuccess === true) {
                    Swal.fire({
                        title: t('general.success'),
                        text: getResponseMessage(res?.message, language),
                        icon: 'success',
                        timer: 3000,
                        showConfirmButton: false,
                    });
                    getGrowList();
                    onModalClose();
                } else {
                    Swal.fire(t('general.anErrorOccured'), t('general.error'), 'error');
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), t('general.error'), 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getGrowList();
    }, []);

    return (
        <SCUIContent>
            <Box className={GROW.page}>
                <div className={GROW.breadcrumbSection}>
                    <SCUIBreadCrumb title={t('growManagement.title')} componentLink={PageUrl.growManagement.url} />
                </div>
                <div className={GROW.loadingSection}>
                    <SCUILoadingLinear display={isLoading} />
                </div>
                <div className={GROW.contentSection}>
                    <GrowListPage setModalOpen={onModalOpen} onLogOpen={onLogOpen} growList={growList} />
                    {open && (
                        <GrowEditModal
                            open={open}
                            onClose={onModalClose}
                            setFormState={setFormState}
                            onUpdate={onUpdate}
                            formState={formState}
                            isLoading={isLoading}
                        />
                    )}
                    {logOpen && (
                        <GrowLogModal open={logOpen} onClose={onLogClose} warehouseCode={selectedLogWarehouseCode} />
                    )}
                </div>
            </Box>
        </SCUIContent>
    );
};
